import { Box, Divider } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useCallback } from 'react';
import Button from '@ui/Button';
import Typography from '@ui/Typography';
import PhoneticLibraryInputSidebar from '@replacements/components/PhoneticLibrary/PhoneticLibraryInputSidebar';
import OnIndicator from '@replacements/components/ReplacementsLibrary/OnIndicator';
import { useCanReadTeamReplacements } from 'abilities/replacements';
import { ProjectFragment } from '../../fragments.generated';
import { useAppSelector } from '../../../store/hooks';
import PhoneticReplacementsSwitch from '../../Editor/Controls/PhoneticReplacementsSwitch';
import {
  EventName,
  useEventsContext,
} from '../../../context/events/EventsContext';
import { usePromptsContext } from '../../../context/prompts/PromptsContext';

const useStyles = makeStyles({ name: 'StudioPhoneticLibrary' })(theme => ({
  divider: {
    marginLeft: -20,
    marginRight: -20,
  },
  replacementsLabelContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  capitalize: {
    textTransform: 'capitalize',
  },
}));

interface PhoneticLibraryProps {
  project: ProjectFragment;
  replacementsDisabled: boolean;
}

const StudioPhoneticLibrary = ({
  project,
  replacementsDisabled,
}: PhoneticLibraryProps) => {
  const { classes } = useStyles();
  const [eventTracking] = useEventsContext();
  const [, dispatchPrompt] = usePromptsContext();
  const canReadTeamReplacements = useCanReadTeamReplacements();

  const replacementsState = useAppSelector(appState => appState.replacements);
  const { replacementsLibrary } = replacementsState;
  const openProjectReplacementsPrompt = useCallback(() => {
    eventTracking.track({
      type: EventName.PromptShown,
      data: {
        prompt: `OpenProjectReplacementsPrompt`,
      },
    });
    dispatchPrompt({
      type: 'OpenProjectReplacementsPrompt',
      payload: {
        projectId: project.id,
        library: replacementsLibrary,
      },
    });
  }, [dispatchPrompt, eventTracking, project.id, replacementsLibrary]);

  return (
    <>
      {!canReadTeamReplacements ? (
        <Box id="phonetic-replacements">
          {!replacementsDisabled && <PhoneticReplacementsSwitch />}
          <Divider className={classes.divider} />
          <Box mb={3} mt={3}>
            <PhoneticLibraryInputSidebar disabled={replacementsDisabled} />
          </Box>
        </Box>
      ) : (
        <div id="shared-libraries-and-phonetic-replacements">
          <Box className={classes.replacementsLabelContainer}>
            <Typography
              variant="h5"
              color={replacementsDisabled ? 'tertiary' : 'primary'}
              className={classes.capitalize}
              bold
            >
              Replacements
            </Typography>
            {replacementsLibrary.length > 0 && <OnIndicator />}
          </Box>
          <Box marginTop={1} marginBottom={4}>
            <Button
              variant="secondary"
              type="button"
              onClick={openProjectReplacementsPrompt}
              size="small"
              fullWidth
              disabled={replacementsDisabled}
            >
              {'Open libraries'}
            </Button>
          </Box>
          <Divider className={classes.divider} />
          <Box mt={3} mb={3}>
            <PhoneticLibraryInputSidebar
              projectId={project.id}
              disabled={replacementsDisabled}
            />
          </Box>
        </div>
      )}
      <Divider className={classes.divider} />
    </>
  );
};

export default StudioPhoneticLibrary;
